/**
 * AudioListener Component
 *
 * Represents the "ears" of the player in the scene.
 * Should be attached to the main camera or player object.
 * Only one AudioListener should be active at a time.
 *
 * The AudioListener reads from the attached Transform component
 * and updates the Web Audio API listener position/orientation.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  executeInEditor,
  field,
  customEditor,
} from "../attributes";

@component("core.AudioListener")
@customEditor("AudioListenerEditor")
@displayName("Audio Listener")
@icon("Headphones")
@color("#22c55e")
@category("Audio")
@allowMultiple(false)
@requireComponent(["core.Transform"])
@executeInEditor(true)
export class AudioListener {
  /**
   * Volume multiplier for the entire listener (global volume).
   * This acts as a master volume for everything this listener hears.
   */
  @field({ type: "f32", label: "Volume", min: 0, max: 2, step: 0.01, default: 1 })
  volume: number = 1;

  /**
   * Whether this listener is active.
   * Only one listener should be active in the scene.
   */
  @field({ type: "bool", label: "Enabled", default: true })
  enabled: boolean = true;

  /**
   * Velocity factor for doppler effect.
   * 0 = no doppler, 1 = normal doppler.
   */
  @field({ type: "f32", label: "Doppler Factor", min: 0, max: 5, step: 0.1, group: "Spatial", default: 0 })
  dopplerFactor: number = 0;

  /**
   * Speed of sound for doppler calculation (units/second).
   * Default ~343 for real-world speed of sound.
   */
  @field({ type: "f32", label: "Speed of Sound", min: 1, max: 10000, step: 1, group: "Spatial", default: 343 })
  speedOfSound: number = 343;

  // =========================================================================
  // ORIENTATION
  // =========================================================================

  /**
   * Invert left/right panning.
   * When true, sounds on the left will be heard on the right and vice-versa.
   */
  @field({ type: "bool", label: "Invert X", group: "Orientation", default: false })
  invertX: boolean = false;

  /**
   * Invert front/back depth.
   * When true, sounds in front will be treated as behind and vice-versa.
   */
  @field({ type: "bool", label: "Invert Y", group: "Orientation", default: false })
  invertY: boolean = false;

  /**
   * Additional rotation offset (degrees) applied to the listener orientation.
   * Useful when the camera is rotated relative to the player.
   */
  @field({ type: "f32", label: "Rotation Offset", min: -180, max: 180, step: 1, group: "Orientation", default: 0 })
  rotationOffset: number = 0;

  /**
   * Whether to follow the transform rotation.
   * When false the listener always faces "screen up" regardless of transform rotation.
   */
  @field({ type: "bool", label: "Follow Rotation", group: "Orientation", default: true })
  followRotation: boolean = true;
}
